import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { LanguageProvider } from "@/context/LanguageContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import AuthCallback from "@/components/AuthCallback";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Services from "@/pages/Services";
import Products from "@/pages/Products";
import Categories from "@/pages/Categories";
import Purchases from "@/pages/Purchases";
import Packages from "@/pages/Packages";
import Customers from "@/pages/Customers";
import Bookings from "@/pages/Bookings";
import POS from "@/pages/POS";
import Inventory from "@/pages/Inventory";
import StockMovements from "@/pages/StockMovements";
import InventoryTransfers from "@/pages/InventoryTransfers";
import Accounts from "@/pages/Accounts";
import Suppliers from "@/pages/Suppliers";
import Users from "@/pages/Users";
import Accounting from "@/pages/Accounting";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import PublicBooking from "@/pages/PublicBooking";
import MembershipCard from "@/pages/MembershipCard";
import Branches from "@/pages/Branches";
import WebsiteCMS from "@/pages/WebsiteCMS";
import PublicSalonSite from "@/pages/PublicSalonSite";
import Coupons from "@/pages/Coupons";
import Reviews from "@/pages/Reviews";
import Commissions from "@/pages/Commissions";
import StaffPayouts from "@/pages/StaffPayouts";
import Analytics from "@/pages/Analytics";
import Notifications from "@/pages/Notifications";
import WhatsAppSettings from "@/pages/WhatsAppSettings";
import WhatsAppCampaigns from "@/pages/WhatsAppCampaigns";
import Departments from "@/pages/Departments";
import Employees from "@/pages/Employees";
import StaffRota from "@/pages/StaffRota";
import Payroll from "@/pages/Payroll";
import Attendance from "@/pages/Attendance";
import CustomerPortal from "@/pages/CustomerPortal";
import InvoiceDesign from "@/pages/InvoiceDesign";
import TenancyAdmin from "@/pages/TenancyAdmin";
import TenantsAdmin from "@/pages/TenantsAdmin";
import TenantDetail from "@/pages/TenantDetail";
import PlatformAdminLayout from "@/components/PlatformAdminLayout";
import PlatformAdminDashboard from "@/pages/PlatformAdminDashboard";
import PlatformAdminSubscriptions from "@/pages/PlatformAdminSubscriptions";
import PlansAdmin from "@/pages/PlansAdmin";
import PlatformAdminBackups from "@/pages/PlatformAdminBackups";
import MarketplaceListing from "@/pages/MarketplaceListing";
import PlatformMarketplace from "@/pages/PlatformMarketplace";
import PlatformAdminMarketplace from "@/pages/PlatformAdminMarketplace";
import { MarketplaceSearch, MarketplaceListingDetail } from "@/pages/MarketplaceDiscover";
import MarketplaceHome from "@/pages/MarketplaceHome";
import MarketplaceResults from "@/pages/MarketplaceResults";
import MarketplaceVenue from "@/pages/MarketplaceVenue";
import OnboardingWizard from "@/pages/OnboardingWizard";
import PlatformWhatsApp from "@/pages/PlatformWhatsApp";
import PlatformReviews from "@/pages/PlatformReviews";
import Signup from "@/pages/Signup";
import PlatformTenantsPending from "@/pages/PlatformTenantsPending";
import { BranchProvider } from "@/context/BranchContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ModuleGuard from "@/components/ModuleGuard";

const ProtectedRoute = ({ children, module, page }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-400">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{(module || page) ? <ModuleGuard module={module} page={page}>{children}</ModuleGuard> : children}</Layout>;
};

// AuthOnlyRoute — same auth gate but WITHOUT wrapping the salon Layout. Used
// by surfaces that bring their own chrome (e.g., the Platform Admin shell).
const AuthOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-400">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const AppRoutes = () => {
  const location = useLocation();
  // CRITICAL: Handle session_id in URL fragment synchronously before other routes
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/book" element={<PublicBooking />} />
      <Route path="/salon/:id" element={<PublicSalonSite />} />
      <Route path="/salons/:id" element={<PublicSalonSite />} />
      <Route path="/salons/:id/services" element={<PublicSalonSite />} />
      <Route path="/salons/:id/offers" element={<PublicSalonSite />} />
      <Route path="/account/:id" element={<CustomerPortal />} />
      <Route path="/account" element={<CustomerPortal />} />
      <Route path="/card/:customerId" element={<MembershipCard />} />
      {/* Public Marketplace (Phase 3) — no auth required */}
      <Route path="/m" element={<MarketplaceHome />} />
      <Route path="/m/search" element={<MarketplaceResults />} />
      <Route path="/m/s/:slug" element={<MarketplaceVenue />} />
      <Route path="/m/c/:city" element={<MarketplaceResults />} />
      <Route path="/m/c/:city/:category" element={<MarketplaceResults />} />
      {/* Legacy routes kept for backwards compatibility */}
      <Route path="/m/legacy" element={<MarketplaceSearch />} />
      <Route path="/m/legacy/s/:slug" element={<MarketplaceListingDetail />} />
      <Route path="/" element={<ProtectedRoute module="dashboard"><Dashboard /></ProtectedRoute>} />
      <Route path="/services" element={<ProtectedRoute module="services"><Services /></ProtectedRoute>} />
      <Route path="/products" element={<ProtectedRoute module="products"><Products /></ProtectedRoute>} />
      <Route path="/categories" element={<ProtectedRoute module="categories"><Categories /></ProtectedRoute>} />
      <Route path="/purchases" element={<ProtectedRoute module="purchases"><Purchases /></ProtectedRoute>} />
      <Route path="/packages" element={<ProtectedRoute module="packages"><Packages /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute module="customers"><Customers /></ProtectedRoute>} />
      <Route path="/bookings" element={<ProtectedRoute module="bookings"><Bookings /></ProtectedRoute>} />
      <Route path="/pos" element={<ProtectedRoute module="pos"><POS /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute module="inventory"><Inventory /></ProtectedRoute>} />
      <Route path="/stock-movements" element={<ProtectedRoute module="stock-movements"><StockMovements /></ProtectedRoute>} />
      <Route path="/inventory-transfers" element={<ProtectedRoute module="stock-movements"><InventoryTransfers /></ProtectedRoute>} />
      <Route path="/accounting" element={<ProtectedRoute module="accounting"><Accounting /></ProtectedRoute>} />
      <Route path="/accounts" element={<ProtectedRoute module="accounts"><Accounts /></ProtectedRoute>} />
      <Route path="/suppliers" element={<ProtectedRoute module="suppliers"><Suppliers /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute module="users"><Users /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute module="reports"><Reports /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute module="settings"><Settings /></ProtectedRoute>} />
      <Route path="/branches" element={<ProtectedRoute module="branches"><Branches /></ProtectedRoute>} />
      <Route path="/website" element={<ProtectedRoute module="website"><WebsiteCMS /></ProtectedRoute>} />
      <Route path="/coupons" element={<ProtectedRoute module="coupons"><Coupons /></ProtectedRoute>} />
      <Route path="/reviews" element={<ProtectedRoute module="reviews"><Reviews /></ProtectedRoute>} />
      <Route path="/commissions" element={<ProtectedRoute module="commissions"><Commissions /></ProtectedRoute>} />
      <Route path="/staff-payouts" element={<ProtectedRoute module="commissions"><StaffPayouts /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute module="analytics"><Analytics /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute module="notifications"><Notifications /></ProtectedRoute>} />
      <Route path="/whatsapp" element={<ProtectedRoute module="whatsapp"><WhatsAppCampaigns /></ProtectedRoute>} />
      <Route path="/whatsapp/settings" element={<ProtectedRoute module="whatsapp"><WhatsAppSettings /></ProtectedRoute>} />
      <Route path="/departments" element={<ProtectedRoute module="departments"><Departments /></ProtectedRoute>} />
      <Route path="/employees" element={<ProtectedRoute module="employees"><Employees /></ProtectedRoute>} />
      <Route path="/staff-rota" element={<ProtectedRoute module="employees"><StaffRota /></ProtectedRoute>} />
      <Route path="/payroll" element={<ProtectedRoute module="employees" page="payroll"><Payroll /></ProtectedRoute>} />
      <Route path="/attendance" element={<ProtectedRoute module="employees" page="attendance"><Attendance /></ProtectedRoute>} />
      <Route path="/invoice-design" element={<ProtectedRoute module="invoice-design"><InvoiceDesign /></ProtectedRoute>} />
      <Route path="/platform/tenancy" element={<ProtectedRoute><TenancyAdmin /></ProtectedRoute>} />
      {/* Legacy direct URLs — keep redirects so bookmarks don't break. */}
      <Route path="/admin/tenants" element={<ProtectedRoute><TenantsAdmin /></ProtectedRoute>} />
      <Route path="/admin/tenants/:tenantId" element={<ProtectedRoute><TenantDetail /></ProtectedRoute>} />
      {/* New Platform Admin shell — its own layout, no salon sidebar. */}
      <Route path="/platform-admin" element={<AuthOnlyRoute><PlatformAdminLayout /></AuthOnlyRoute>}>
        <Route index element={<PlatformAdminDashboard />} />
        <Route path="tenants" element={<TenantsAdmin />} />
        <Route path="tenants/:tenantId" element={<TenantDetail />} />
        <Route path="subscriptions" element={<PlatformAdminSubscriptions />} />
        <Route path="plans" element={<PlansAdmin />} />
        <Route path="backups" element={<PlatformAdminBackups />} />
        <Route path="marketplace" element={<PlatformAdminMarketplace />} />
      </Route>
      <Route path="/platform/marketplace" element={<ProtectedRoute><PlatformMarketplace /></ProtectedRoute>} />
      <Route path="/platform/onboarding" element={<ProtectedRoute><OnboardingWizard /></ProtectedRoute>} />
      <Route path="/platform/whatsapp" element={<ProtectedRoute><PlatformWhatsApp /></ProtectedRoute>} />
      <Route path="/platform/reviews" element={<ProtectedRoute><PlatformReviews /></ProtectedRoute>} />
      <Route path="/platform/tenants/pending" element={<ProtectedRoute><PlatformTenantsPending /></ProtectedRoute>} />
      <Route path="/marketplace/listing" element={<ProtectedRoute><MarketplaceListing /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <BrowserRouter>
          <AuthProvider>
            <ThemeProvider>
              <BranchProvider>
                <AppRoutes />
                <Toaster position="top-center" richColors theme="light" />
              </BranchProvider>
            </ThemeProvider>
          </AuthProvider>
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}
